// components/MockInterview/MockInterviewHistory.js
import React, { useEffect, useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Mic, Search, Eye, RotateCcw, Calendar, Award,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import {
  PageHeader, AppCard, AppCardContent,
  UserAvatar, EmptyState,
} from "../ui";
import "./mock-interview.scss";

const LIMIT = 10;

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
  });
}

function scoreClass(s) {
  if (s >= 80) return "mi-cell-score--high";
  if (s >= 50) return "mi-cell-score--mid";
  return "mi-cell-score--low";
}

function getPaginationNumbers(current, total) {
  const pages = [];
  if (total <= 5) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }
  pages.push(1);
  if (current > 3) pages.push("...");
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push("...");
  pages.push(total);
  return pages;
}

const MockInterviewHistory = ({ isAdmin }) => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  // Data
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

  // Filters (admin only)
  const [studentName, setStudentName] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [batches, setBatches] = useState([]);

  // Current page
  const [page, setPage] = useState(1);

  // Debounced name for search-as-you-type
  const [debouncedName, setDebouncedName] = useState("");

  // Debounce student name input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(studentName);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [studentName]);

  // Reset page when batch changes
  useEffect(() => {
    setPage(1);
  }, [batchFilter]);

  // Load batches list for admin filter
  useEffect(() => {
    if (!isAdmin) return;
    apiClient.get("/batches/list?pageSize=200")
      .then(({ data }) => setBatches(data.batches || []))
      .catch(() => setBatches([]));
  }, [isAdmin]);

  // Fetch interviews (server-side)
  const fetchInterviews = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (isAdmin) {
        const params = new URLSearchParams({ page, limit: LIMIT });
        if (debouncedName) params.set("studentName", debouncedName);
        if (batchFilter) params.set("batch", batchFilter);
        url = `/mock-interview/all?${params.toString()}`;
      } else {
        url = `/mock-interview/student/${currentUser.studentId}?page=${page}&limit=${LIMIT}`;
      }
      const { data } = await apiClient.get(url);
      setInterviews(data.data || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch {
      setInterviews([]);
    }
    setLoading(false);
  }, [isAdmin, page, debouncedName, batchFilter, currentUser?.studentId]);

  useEffect(() => {
    fetchInterviews();
  }, [fetchInterviews]);

  // Navigation helpers
  const viewPrefix = isAdmin ? "/mock-interview" : "/s/mock-interview";

  return (
    <div className="mi-page">
      <PageHeader
        title="Mock Interview History"
        description={isAdmin ? "View all student mock interviews" : "Review your past mock interviews"}
        icon={Mic}
      />

      {/* Admin filters */}
      {isAdmin && (
        <div className="mi-filters">
          <div className="mi-search">
            <Search size={16} className="mi-search__icon" />
            <input
              type="text"
              className="mi-search__input"
              placeholder="Search by student name..."
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
            />
          </div>
          <select
            className="mi-filter-select"
            value={batchFilter}
            onChange={(e) => setBatchFilter(e.target.value)}
          >
            <option value="">All Batches</option>
            {batches.map((b) => (
              <option key={b._id} value={b._id}>
                {b.name || b.course?.name || b._id}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Table */}
      <AppCard className="mi-table-card">
        <AppCardContent className="p-0">
          {loading ? (
            <div className="mi-loading">
              <Spinner animation="border" size="sm" />
              <span>Loading mock interviews...</span>
            </div>
          ) : interviews.length === 0 ? (
            <EmptyState
              icon={<Mic size={40} />}
              title="No mock interviews found"
              description={isAdmin ? "Try adjusting your filters" : "Start a mock interview to see your history here."}
            />
          ) : (
            <div className="mi-table-wrapper">
              <table className="mi-table">
                <thead>
                  <tr>
                    <th>#</th>
                    {isAdmin && <th>Student</th>}
                    <th>Date</th>
                    <th>Technology</th>
                    <th>Role</th>
                    <th>Difficulty</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {interviews.map((m, i) => {
                    const rowNum = (pagination.page - 1) * LIMIT + i + 1;
                    return (
                      <tr key={m._id}>
                        <td>{rowNum}</td>
                        {isAdmin && (
                          <td>
                            <div
                              className="mi-cell-user"
                              onClick={() => m.student?._id && navigate(`/students/${m.student._id}`)}
                            >
                              <UserAvatar name={m.student?.name || "?"} size="sm" />
                              <div className="mi-cell-user__details">
                                <span className="mi-cell-user__name">{m.student?.name || "—"}</span>
                                {m.student?.email && (
                                  <span className="mi-cell-user__email">{m.student.email}</span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}
                        <td className="mi-cell-date">
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Calendar size={13} />
                            {formatDate(m.createdAt)}
                          </span>
                        </td>
                        <td className="mi-cell-topics">
                          {m.config?.technology || m.config?.topics?.join(", ") || "-"}
                        </td>
                        <td>{m.config?.role || m.config?.experience || "-"}</td>
                        <td>{m.config?.difficulty || "-"}</td>
                        <td>
                          {m.status === "completed" && m.overallScore != null ? (
                            <span className={`mi-cell-score ${scoreClass(Math.round(m.overallScore))}`}>
                              <Award size={13} style={{ marginRight: 3 }} />
                              {Math.round(m.overallScore)}
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-tertiary)" }}>—</span>
                          )}
                        </td>
                        <td>
                          <span className={`mi-status mi-status--${m.status}`}>
                            {m.status === "completed" ? "Completed" : "In Progress"}
                          </span>
                        </td>
                        <td className="mi-cell-actions">
                          <button
                            className="mi-action-btn"
                            onClick={() => navigate(`${viewPrefix}/summary/${m._id}`)}
                          >
                            <Eye size={13} /> View
                          </button>
                          {m.status !== "completed" && !isAdmin && (
                            <button
                              className="mi-action-btn mi-action-btn--resume"
                              onClick={() => navigate(`${viewPrefix}/session/${m._id}`)}
                            >
                              <RotateCcw size={13} /> Resume
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && pagination.pages > 1 && (
            <div className="mi-pagination">
              <span className="mi-pagination__info">
                Showing {interviews.length === 0 ? 0 : (pagination.page - 1) * LIMIT + 1} to{" "}
                {(pagination.page - 1) * LIMIT + interviews.length} of {pagination.total}
              </span>
              <div className="mi-pagination__controls">
                <button
                  className="mi-pagination__btn"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  ‹
                </button>
                {getPaginationNumbers(page, pagination.pages).map((num, idx) =>
                  num === "..." ? (
                    <span key={`e-${idx}`} className="mi-pagination__ellipsis">…</span>
                  ) : (
                    <button
                      key={`p-${num}`}
                      className={`mi-pagination__btn${num === page ? " active" : ""}`}
                      onClick={() => setPage(num)}
                    >
                      {num}
                    </button>
                  )
                )}
                <button
                  className="mi-pagination__btn"
                  disabled={page === pagination.pages}
                  onClick={() => setPage(page + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>
    </div>
  );
};

export default MockInterviewHistory;
